import Phaser from "phaser";
import {
  ArtPipeline,
  affiliationFromTeamMap,
  facingDirFromAim,
  type AnimState,
} from "../../../art";
import { portalFfaCatalog } from "../../../data/portal-ffa-catalog";
import {
  resolveAttackFx,
  resolveHitPreset,
  playTimedAttackFx,
  VfxPlayer,
  type AttackFxTables,
} from "../../../vfx";
import type { SimEvent } from "../sim-events";
import { FfaEvent } from "../sim-events";
import {
  type SimEntity,
  type UnitRole,
  type WorldState,
} from "../index";
import {
  UnitRig,
  createHpBar,
  portalFfaGripPack,
  updateHpBar,
  type RigTables,
} from "../../../view/unit-rig";
import { swingSecFor } from "../sim/combat-ai";
import {
  coneTelegraphPoints,
  hitVfxDeferredToAttack,
  isArtilleryRole,
  rangedTravelMs,
  resolveAttackOrigin,
} from "./combat-vfx";
import { hexToCss } from "./camera-gesture";
import {
  deathTtlSec,
  dyingIdsOverBudget,
  shouldPurgeDyingView,
} from "./presentation-tracking";
import { stepDisplayPos } from "./presentation-smooth";

const ART_PACK = "portal-ffa";
/** Max full hit VFX (streak/float/lunge) per applyEvents batch. */
const HIT_VFX_BUDGET = 8;
/** Max attack telegraph / projectile / impact spawn per applyEvents batch. */
const ATTACK_VFX_BUDGET = 10;
const VFX_DEPTH = 6;
const VFX_FLOAT_DEPTH = 12;
const CELL_PX = 64;

/** Roles with Dropfront-seeded authored dirs + grips. */
function hasAuthoredDirs(role: UnitRole): boolean {
  return role !== "weapon";
}

type EntityView = {
  root: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  rig: UnitRig | null;
  hpBg: Phaser.GameObjects.Rectangle;
  hpGhost: Phaser.GameObjects.Rectangle;
  hpFill: Phaser.GameObjects.Rectangle;
  tag: Phaser.GameObjects.Text | null;
  dying: boolean;
  radius: number;
  spriteId: string;
  affiliation: string;
  teamId: number;
  hpColor: number;
  animState: AnimState;
  animTime: number;
  aimAngle: number;
  lastFrameKey: string;
  role: UnitRole;
};

/** Player = ally; each bot team gets its own enemyN rim key. */
const FFA_AFFILIATION_BY_TEAM: Readonly<Record<number, string>> = {
  0: "ally",
  1: "enemy1",
  2: "enemy2",
  3: "enemy3",
  4: "enemy4",
  5: "enemy5",
};

export function affiliationForTeam(teamId: number): string {
  if (teamId <= 0) return "ally";
  const n = ((teamId - 1) % 5) + 1;
  return affiliationFromTeamMap(n, FFA_AFFILIATION_BY_TEAM, `enemy${n}`);
}

export function teamColor(teamId: number): number {
  const colors = portalFfaCatalog.teamColors;
  if (!colors.length) return 0xff3d7a;
  const idx = Math.max(0, teamId) % colors.length;
  return colors[idx]!;
}

const HP_DIM = 0x1a1a22;

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function lerpRgb(c0: number, c1: number, t: number): number {
  const tClamped = Math.max(0, Math.min(1, t));
  const r0 = (c0 >> 16) & 0xff;
  const g0 = (c0 >> 8) & 0xff;
  const b0 = c0 & 0xff;
  const r1 = (c1 >> 16) & 0xff;
  const g1 = (c1 >> 8) & 0xff;
  const b1 = c1 & 0xff;
  return (
    (lerpChannel(r0, r1, tClamped) << 16) |
    (lerpChannel(g0, g1, tClamped) << 8) |
    lerpChannel(b0, b1, tClamped)
  );
}

/** Faction hue only: full HP = team color, low HP = same hue darkened (no green→red). */
export function hpFillColor(teamId: number, ratio: number): number {
  const base = teamColor(teamId);
  const r = Math.max(0, Math.min(1, ratio));
  return lerpRgb(base, HP_DIM, (1 - r) * 0.82);
}

function hpHitFlashColor(teamId: number): number {
  return lerpRgb(teamColor(teamId), 0xffffff, 0.65);
}

function spriteIdForRole(role: UnitRole): string {
  return role;
}

/** Master display size: player > bot master > troops. */
function bodyDisplayDiam(e: SimEntity): number {
  const base = Math.max(16, e.radius * 2.2);
  if (e.role === "master") {
    return e.teamId === 0 ? base * 1.38 : base * 1.2;
  }
  if (e.role === "hero") return base * 1.15;
  if (e.role === "weapon") return base * 1.1;
  return base;
}

function masterBladeColor(teamId: number): number | undefined {
  // Player keeps catalog gold; bots tint blade by team.
  if (teamId === 0) return undefined;
  return teamColor(teamId);
}

function rigTables(): RigTables | undefined {
  return portalFfaCatalog.rig as RigTables | undefined;
}

/**
 * FFA presentation — authored facing×state body + UnitRig grip weapon (Dropfront spine).
 */
export class FfaPresentation {
  private readonly scene: Phaser.Scene;
  private readonly vfx: VfxPlayer;
  private readonly views = new Map<number, EntityView>();
  private overlay!: Phaser.GameObjects.Graphics;
  private hitVfxLeft = HIT_VFX_BUDGET;
  private attackVfxLeft = ATTACK_VFX_BUDGET;
  private readonly ellipsePool: Phaser.GameObjects.Ellipse[] = [];
  private readonly graphicsPool: Phaser.GameObjects.Graphics[] = [];
  private readonly circlePool: Phaser.GameObjects.Arc[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.vfx = new VfxPlayer(scene);
    this.overlay = scene.add.graphics().setDepth(0);
  }

  destroy(): void {
    for (const v of this.views.values()) v.root.destroy(true);
    this.views.clear();
    this.overlay.destroy();
    for (const e of this.ellipsePool) e.destroy();
    this.ellipsePool.length = 0;
    for (const g of this.graphicsPool) g.destroy();
    this.graphicsPool.length = 0;
    for (const c of this.circlePool) c.destroy();
    this.circlePool.length = 0;
  }

  /** Live + dying view count — for tracking regression tests / stall diagnostics. */
  trackingStats(): { views: number; dying: number } {
    let dying = 0;
    for (const v of this.views.values()) {
      if (v.dying) dying += 1;
    }
    return { views: this.views.size, dying };
  }

  private destroyView(id: number): void {
    const view = this.views.get(id);
    if (!view) return;
    view.root.destroy(true);
    this.views.delete(id);
  }

  applyEvents(events: SimEvent[]): void {
    this.hitVfxLeft = HIT_VFX_BUDGET;
    this.attackVfxLeft = ATTACK_VFX_BUDGET;
    for (const ev of events) {
      switch (ev.kind) {
        case FfaEvent.Attack:
          this.onAttack(ev);
          break;
        case FfaEvent.Hit:
          this.onHit(ev);
          break;
        case FfaEvent.Death:
          this.onDeath(ev);
          break;
        case FfaEvent.Summon:
          this.vfx.play("ffa.summon", { x: ev.x, y: ev.y });
          break;
        case FfaEvent.ManaCollect:
          this.vfx.play(ev.jackpot ? "ffa.mana_jackpot" : "ffa.mana_collect", {
            x: ev.x,
            y: ev.y,
            vars: { value: ev.value },
          });
          break;
        case FfaEvent.MasterDrop:
          this.vfx.play("ffa.master_drop", {
            x: ev.x,
            y: ev.y,
            vars: { value: ev.value },
          });
          break;
        case FfaEvent.GoalSet:
          break;
      }
    }
  }

  pulseGoal(marker: Phaser.GameObjects.Arc): void {
    marker.setScale(1);
    marker.setAlpha(0.35);
    this.vfx.play("ffa.goal_pulse", {
      x: marker.x,
      y: marker.y,
      targets: { goal: marker },
    });
  }

  syncViews(world: WorldState): void {
    const live = new Set(world.entities.map((e) => e.id));
    for (const [id, view] of this.views) {
      if (!live.has(id) && !view.dying) {
        this.destroyView(id);
      }
    }

    const trailBudget = {
      left: Number(portalFfaCatalog.rig?.trailBudgetPerFrame) || 12,
    };
    const followHz =
      Number(
        (portalFfaCatalog.crowd as { displayPosFollowHz?: number })
          .displayPosFollowHz,
      ) || 14;
    const snapPx =
      Number(
        (portalFfaCatalog.crowd as { displayPosSnapPx?: number })
          .displayPosSnapPx,
      ) || 48;
    const dt = 1 / 30;

    for (const e of world.entities) {
      let view = this.views.get(e.id);
      if (!view) {
        view = this.createView(e);
        this.views.set(e.id, view);
      }
      if (view.dying) continue;
      const pos = stepDisplayPos(
        view.root.x,
        view.root.y,
        e.x,
        e.y,
        followHz,
        snapPx,
        dt,
      );
      view.root.setPosition(pos.x, pos.y);
      if (!this.scene.tweens.isTweening(view.body)) {
        view.body.setX(0);
        // Y is driven by rig bob when present.
        if (!view.rig) view.body.setY(0);
      }
      if (!this.scene.tweens.isTweening(view.root)) {
        view.root.setScale(1);
      }

      view.animState = e.animState;
      view.animTime = e.animTime;
      view.aimAngle = e.aimAngle;
      if (view.rig && hasAuthoredDirs(e.role)) {
        this.updateCombatRig(view, e, trailBudget);
      } else {
        this.applyIdleBody(view);
        if (view.rig) {
          view.rig.update(
            {
              aimAngle: e.aimAngle,
              animState: e.animState,
              animTime: e.animTime,
              intentVx: e.intentVx,
              intentVy: e.intentVy,
              facing: e.facing,
              dying: false,
            },
            trailBudget,
            this.scene,
            { x: e.x, y: e.y },
          );
        } else {
          view.body.setFlipX(e.facing < 0);
        }
      }

      this.updateHpBar(view, e.hp / e.maxHp, e.role === "master");
    }

    const dyingEntries: Array<{ id: number; animTime: number }> = [];
    for (const [id, view] of this.views) {
      if (!view.dying) continue;
      view.animTime += 1 / 30;
      view.rig?.update(
        {
          aimAngle: view.aimAngle,
          animState: "death",
          animTime: view.animTime,
          intentVx: 0,
          intentVy: 0,
          facing: 1,
          dying: true,
          lockBodyFlip: hasAuthoredDirs(view.role),
          facingDir: hasAuthoredDirs(view.role)
            ? facingDirFromAim(view.aimAngle)
            : undefined,
        },
        { left: 0 },
        this.scene,
        { x: view.root.x, y: view.root.y },
      );
      const { key, clipDone } = ArtPipeline.ensureAnimFrame(
        this.scene,
        ART_PACK,
        view.spriteId,
        view.affiliation,
        "death",
        view.animTime,
        hasAuthoredDirs(view.role)
          ? facingDirFromAim(view.aimAngle)
          : undefined,
      );
      if (key !== view.lastFrameKey) {
        view.body.setTexture(key);
        view.lastFrameKey = key;
        view.rig?.setBodyTexture(key);
      }
      const ttl = deathTtlSec(view.role);
      if (shouldPurgeDyingView(view.animTime, clipDone, ttl)) {
        this.destroyView(id);
        continue;
      }
      dyingEntries.push({ id, animTime: view.animTime });
    }

    for (const id of dyingIdsOverBudget(dyingEntries)) {
      this.destroyView(id);
    }
  }

  drawStaticOverlay(world: WorldState): void {
    const g = this.overlay;
    g.clear();
    const al = world.alliance;
    if (al && world.time <= al.endsAt) {
      g.lineStyle(2, 0x66ffcc, 0.45);
      g.strokeCircle(al.centerX, al.centerY, al.radius);
    }
    const core = world.warpCore;
    if (core) {
      g.fillStyle(0xff66aa, 0.2);
      g.fillCircle(core.x, core.y, core.radius);
      g.lineStyle(2, 0xff66aa, 0.7);
      g.strokeCircle(core.x, core.y, core.radius * 0.3);
    }
    for (const orb of world.orbs) {
      const ready = world.time >= orb.collectableAt;
      const r = 4 + Math.min(10, orb.value / 8);
      g.fillStyle(ready ? 0x6ee7ff : 0x3a6a80, ready ? 0.9 : 0.4);
      g.fillCircle(orb.x, orb.y, r);
    }

    const auraR =
      Number(
        (portalFfaCatalog.units.hero as { auraRadius?: number }).auraRadius,
      ) || 120;
    for (const e of world.entities) {
      const tint = teamColor(e.teamId);
      if (e.role === "hero") {
        g.lineStyle(2, tint, 0.28);
        g.strokeCircle(e.x, e.y, auraR);
      } else if (e.role === "weapon") {
        g.lineStyle(1.5, tint, 0.32);
        g.strokeCircle(e.x, e.y, e.atkRange);
      }
    }
  }

  /** Living combat: body clip by facing × animState × time; weapon from UnitRig. */
  private applyCombatBody(view: EntityView, aimAngle: number): void {
    const facing = facingDirFromAim(aimAngle);
    const state: AnimState = view.dying ? "death" : view.animState;
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      ART_PACK,
      view.spriteId,
      view.affiliation,
      state,
      view.animTime,
      facing,
    );
    if (key !== view.lastFrameKey) {
      view.body.setTexture(key);
      view.lastFrameKey = key;
      view.rig?.setBodyTexture(key);
    }
  }

  private updateCombatRig(
    view: EntityView,
    e: SimEntity,
    trailBudget: { left: number },
  ): void {
    if (!view.rig) return;
    this.applyCombatBody(view, e.aimAngle);
    view.rig.update(
      {
        aimAngle: e.aimAngle,
        animState: e.animState,
        animTime: e.animTime,
        intentVx: e.intentVx,
        intentVy: e.intentVy,
        facing: e.facing,
        dying: false,
        lockBodyFlip: true,
        facingDir: facingDirFromAim(e.aimAngle),
      },
      trailBudget,
      this.scene,
      { x: e.x, y: e.y },
    );
  }

  /** Turret / non-dir props: idle bake only. */
  private applyIdleBody(view: EntityView): void {
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      ART_PACK,
      view.spriteId,
      view.affiliation,
      "idle",
      0,
    );
    if (key !== view.lastFrameKey) {
      view.body.setTexture(key);
      view.lastFrameKey = key;
    }
  }

  private onAttack(ev: Extract<SimEvent, { kind: typeof FfaEvent.Attack }>): void {
    const attacker = this.views.get(ev.attackerId);
    const victim = this.views.get(ev.targetId);
    const muzzle =
      attacker?.rig && !attacker.dying
        ? attacker.rig.muzzleWorld({
            x: attacker.root.x,
            y: attacker.root.y,
          })
        : null;
    const origin = resolveAttackOrigin(ev.x0, ev.y0, muzzle);
    const ox = origin.x;
    const oy = origin.y;
    const angle = Math.atan2(ev.y1 - oy, ev.x1 - ox);
    const tint = teamColor(ev.teamId);
    const fx = resolveAttackFx(
      portalFfaCatalog.attackFx as AttackFxTables | undefined,
      ev.role,
    );
    const affiliation =
      attacker?.affiliation ?? affiliationForTeam(ev.teamId);
    const hitTargets = {
      victim: victim?.root,
      victimBody: victim?.body,
      attacker: attacker && !attacker.dying ? attacker.body : undefined,
    };

    if (ev.ranged) {
      const artillery = isArtilleryRole(ev.role);
      const catalogMs = Number(
        (portalFfaCatalog as { fx?: { projectileMs?: number } }).fx
          ?.projectileMs,
      );
      const travelMs = rangedTravelMs(
        ox,
        oy,
        ev.x1,
        ev.y1,
        artillery,
        catalogMs > 0 ? catalogMs : undefined,
      );
      playTimedAttackFx({
        scene: this.scene,
        vfx: this.vfx,
        fx,
        affiliation,
        packId: fx.spriteFxPackId ?? ART_PACK,
        originX: ox,
        originY: oy,
        impactX: ev.x1,
        impactY: ev.y1,
        angle,
        swingSec: swingSecFor(ev.role as UnitRole),
        cellPx: CELL_PX,
        travelOverrideSec: travelMs / 1000,
        depth: VFX_DEPTH,
        floatDepth: VFX_FLOAT_DEPTH,
        hitTargets,
        damage: 0,
        targetKind: "enemy",
        skipDefaultHit: true,
        onBoltFlight: ({ travelMs: ms }) => {
          const spawned = this.spawnProjectileTrail(
            ox,
            oy,
            ev.x1,
            ev.y1,
            angle,
            tint,
            artillery,
          );
          void spawned;
          void ms;
        },
      });
      return;
    }

    if (ev.role === "hero") {
      this.flashCone(ox, oy, angle, 72, 100, tint);
    }
    playTimedAttackFx({
      scene: this.scene,
      vfx: this.vfx,
      fx,
      affiliation,
      packId: fx.spriteFxPackId ?? ART_PACK,
      originX: ox,
      originY: oy,
      impactX: ev.x1,
      impactY: ev.y1,
      angle,
      swingSec: swingSecFor(ev.role as UnitRole),
      cellPx: CELL_PX,
      depth: VFX_DEPTH,
      floatDepth: VFX_FLOAT_DEPTH,
      hitTargets,
      damage: 0,
      targetKind: "enemy",
      skipDefaultHit: true,
    });
  }

  private takeAttackBudget(): boolean {
    if (this.attackVfxLeft <= 0) return false;
    this.attackVfxLeft -= 1;
    return true;
  }

  private acquireEllipse(
    x: number,
    y: number,
    w: number,
    h: number,
    tint: number,
  ): Phaser.GameObjects.Ellipse | null {
    if (!this.takeAttackBudget()) return null;
    let bolt = this.ellipsePool.pop();
    if (!bolt) {
      bolt = this.scene.add.ellipse(x, y, w, h, tint, 0.95);
    } else {
      bolt.setPosition(x, y);
      bolt.setSize(w, h);
      bolt.setFillStyle(tint, 0.95);
      bolt.setAlpha(1);
      bolt.setVisible(true);
      bolt.setActive(true);
      bolt.setScale(1);
    }
    bolt.setDepth(6);
    return bolt;
  }

  private releaseEllipse(bolt: Phaser.GameObjects.Ellipse): void {
    this.scene.tweens.killTweensOf(bolt);
    bolt.setVisible(false);
    bolt.setActive(false);
    this.ellipsePool.push(bolt);
  }

  private acquireGraphics(): Phaser.GameObjects.Graphics | null {
    if (!this.takeAttackBudget()) return null;
    let g = this.graphicsPool.pop();
    if (!g) {
      g = this.scene.add.graphics();
    } else {
      g.clear();
      g.setAlpha(1);
      g.setVisible(true);
      g.setActive(true);
    }
    g.setDepth(4);
    return g;
  }

  private releaseGraphics(g: Phaser.GameObjects.Graphics): void {
    this.scene.tweens.killTweensOf(g);
    g.clear();
    g.setVisible(false);
    g.setActive(false);
    this.graphicsPool.push(g);
  }

  private acquireImpactCircle(
    x: number,
    y: number,
    tint: number,
    radius: number,
  ): Phaser.GameObjects.Arc | null {
    if (!this.takeAttackBudget()) return null;
    let ring = this.circlePool.pop();
    if (!ring) {
      ring = this.scene.add
        .circle(x, y, radius, tint, 0.35)
        .setStrokeStyle(2, tint, 0.85);
    } else {
      ring.setPosition(x, y);
      ring.setRadius(radius);
      ring.setFillStyle(tint, 0.35);
      ring.setStrokeStyle(2, tint, 0.85);
      ring.setAlpha(1);
      ring.setScale(1);
      ring.setVisible(true);
      ring.setActive(true);
    }
    ring.setDepth(5);
    return ring;
  }

  private releaseCircle(ring: Phaser.GameObjects.Arc): void {
    this.scene.tweens.killTweensOf(ring);
    ring.setVisible(false);
    ring.setActive(false);
    this.circlePool.push(ring);
  }

  private flashCone(
    x: number,
    y: number,
    aim: number,
    range: number,
    coneDeg: number,
    tint: number,
  ): void {
    const g = this.acquireGraphics();
    if (!g) return;
    const pts = coneTelegraphPoints(x, y, aim, range, coneDeg, 10);
    g.fillStyle(tint, 0.22);
    g.beginPath();
    g.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) {
      g.lineTo(pts[i]!.x, pts[i]!.y);
    }
    g.closePath();
    g.fillPath();
    this.scene.tweens.add({
      targets: g,
      alpha: 0,
      duration: 180,
      onComplete: () => {
        this.releaseGraphics(g);
      },
    });
  }

  private flashImpactCircle(
    x: number,
    y: number,
    tint: number,
    radius: number,
  ): void {
    const ring = this.acquireImpactCircle(x, y, tint, radius);
    if (!ring) return;
    this.scene.tweens.add({
      targets: ring,
      scale: 1.85,
      alpha: 0,
      duration: 280,
      onComplete: () => {
        this.releaseCircle(ring);
      },
    });
  }

  private spawnProjectileTrail(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    angle: number,
    tint: number,
    artillery = false,
    onArrive?: () => void,
  ): boolean {
    const ms = artillery
      ? rangedTravelMs(x0, y0, x1, y1, true)
      : Number(
          (portalFfaCatalog as { fx?: { projectileMs?: number } }).fx
            ?.projectileMs,
        ) || rangedTravelMs(x0, y0, x1, y1, false);
    const w = artillery ? 18 : 14;
    const h = artillery ? 9 : 5;
    const bolt = this.acquireEllipse(x0, y0, w, h, tint);
    if (!bolt) return false;
    bolt.setRotation(angle);
    if (artillery) {
      bolt.setStrokeStyle(2, 0xfff3c4, 0.7);
    } else {
      bolt.setStrokeStyle(0);
    }
    this.scene.tweens.add({
      targets: bolt,
      x: x1,
      y: y1,
      duration: ms,
      ease: artillery ? "Cubic.easeIn" : "Cubic.easeOut",
      onComplete: () => {
        this.releaseEllipse(bolt);
        if (artillery) this.flashImpactCircle(x1, y1, tint, 48);
        onArrive?.();
      },
    });
    return true;
  }

  private onHit(ev: Extract<SimEvent, { kind: typeof FfaEvent.Hit }>): void {
    const victim = this.views.get(ev.targetId);
    const attacker = this.views.get(ev.attackerId);
    const flashMs =
      Number(
        (portalFfaCatalog as { fx?: { hitFlashMs?: number } }).fx?.hitFlashMs,
      ) || 60;
    if (victim && !victim.dying) {
      const ratio = ev.hp / ev.maxHp;
      this.updateHpBar(victim, ratio, ev.targetRole === "master");
      victim.hpFill.setFillStyle(hpHitFlashColor(victim.teamId), 1);
      this.scene.time.delayedCall(flashMs, () => {
        if (!victim.dying && victim.hpFill.active) {
          victim.hpFill.setFillStyle(hpFillColor(victim.teamId, ratio), 1);
        }
      });
    }
    if (this.hitVfxLeft <= 0) return;
    this.hitVfxLeft -= 1;

    const ax = attacker && !attacker.dying ? attacker.root.x : ev.x;
    const ay = attacker && !attacker.dying ? attacker.root.y : ev.y;
    const angle = Math.atan2(ev.y - ay, ev.x - ax);
    const fx = resolveAttackFx(
      portalFfaCatalog.attackFx as AttackFxTables | undefined,
      ev.attackerRole,
    );
    const preset = resolveHitPreset(fx);
    const tint = teamColor(ev.teamId);
    const playImpact = () => {
      this.vfx.play(preset, {
        x: ev.x,
        y: ev.y,
        angle,
        tint,
        targets: {
          victim: victim?.root,
          victimBody: victim?.body,
          attacker:
            attacker && !attacker.dying ? attacker.body : undefined,
        },
        vars: {
          damage: ev.damage,
          color: hexToCss(tint),
        },
        depth: VFX_DEPTH,
        floatDepth: VFX_FLOAT_DEPTH,
      });
    };

    // Ranged: delay impact VFX to match projectile travel (damage already applied).
    if (hitVfxDeferredToAttack(ev.attackerRole)) {
      const muzzle =
        attacker?.rig && !attacker.dying
          ? attacker.rig.muzzleWorld({
              x: attacker.root.x,
              y: attacker.root.y,
            })
          : null;
      const origin = resolveAttackOrigin(ax, ay, muzzle);
      const travelMs = rangedTravelMs(
        origin.x,
        origin.y,
        ev.x,
        ev.y,
        isArtilleryRole(ev.attackerRole),
      );
      this.scene.time.delayedCall(travelMs, playImpact);
      return;
    }

    playImpact();
  }

  private onDeath(ev: Extract<SimEvent, { kind: typeof FfaEvent.Death }>): void {
    let view = this.views.get(ev.entityId);
    if (!view) {
      const aff = affiliationForTeam(ev.teamId);
      const spriteId = ev.isMaster
        ? "master"
        : ev.role === "hero" || ev.role === "weapon"
          ? ev.role
          : "soldier";
      const { key } = ArtPipeline.ensureAnimFrame(
        this.scene,
        ART_PACK,
        spriteId,
        aff,
        "death",
        0,
      );
      const root = this.scene.add.container(ev.x, ev.y).setDepth(3);
      const body = this.scene.add.image(0, 0, key).setOrigin(0.5);
      body.setDisplaySize(24, 24);
      root.add(body);
      view = {
        root,
        body,
        rig: null,
        hpBg: this.scene.add.rectangle(0, 0, 1, 1, 0, 0),
        hpGhost: this.scene.add.rectangle(0, 0, 1, 1, 0, 0),
        hpFill: this.scene.add.rectangle(0, 0, 1, 1, 0, 0),
        tag: null,
        dying: true,
        radius: 12,
        spriteId,
        affiliation: aff,
        teamId: ev.teamId,
        hpColor: teamColor(ev.teamId),
        animState: "death",
        animTime: 0,
        aimAngle: 0,
        lastFrameKey: key,
        role: ev.isMaster ? "master" : "soldier",
      };
      this.views.set(ev.entityId, view);
    } else {
      view.dying = true;
      view.animState = "death";
      view.animTime = 0;
      view.hpBg.setVisible(false);
      view.hpGhost.setVisible(false);
      view.hpFill.setVisible(false);
      view.tag?.setVisible(false);
      view.rig?.setPartsVisible(false);
      view.body.setVisible(true);
    }

    this.vfx.play(ev.isMaster ? "ffa.master_death" : "ffa.death", {
      x: ev.x,
      y: ev.y,
      targets: { self: view.root },
    });
  }

  private createView(e: SimEntity): EntityView {
    const root = this.scene.add.container(e.x, e.y).setDepth(2);
    const spriteId = spriteIdForRole(e.role);
    const affiliation = affiliationForTeam(e.teamId);
    const ratio = e.hp / e.maxHp;
    const hpColor = hpFillColor(e.teamId, ratio);
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      ART_PACK,
      spriteId,
      affiliation,
      "idle",
      0,
      hasAuthoredDirs(e.role) ? facingDirFromAim(e.aimAngle) : undefined,
    );
    const body = this.scene.add.image(0, 0, key).setOrigin(0.5);
    const diam = bodyDisplayDiam(e);
    body.setDisplaySize(diam, diam);

    const rig = new UnitRig(
      this.scene,
      body,
      e.role,
      rigTables(),
      portalFfaCatalog.crowd.walkEnter,
      e.role === "master" ? masterBladeColor(e.teamId) : undefined,
      hasAuthoredDirs(e.role) ? portalFfaGripPack : null,
    );
    root.add(rig.container);

    const barW =
      e.role === "master" ? 44 : e.role === "hero" || e.role === "weapon" ? 32 : 20;
    const barH = e.role === "master" ? 5 : e.role === "hero" || e.role === "weapon" ? 4 : 3;
    const barY =
      -diam / 2 -
      (e.role === "master" ? 12 : e.role === "hero" || e.role === "weapon" ? 10 : 6);
    const { bg: hpBg, ghost: hpGhost, fill: hpFill } = createHpBar(this.scene, {
      barW,
      barH,
      y: barY,
      bgColor: 0x222222,
      fillColor: hpColor,
      bgAlpha: 0.7,
      initialRatio: ratio,
    });
    root.add([hpBg, hpGhost, hpFill]);

    let tag: Phaser.GameObjects.Text | null = null;
    if (e.role === "master" && e.teamId === 0) {
      tag = this.scene.add
        .text(0, -diam / 2 - 22, "YOU", {
          fontFamily: "Segoe UI, Arial, sans-serif",
          fontSize: "11px",
          fontStyle: "bold",
          color: "#ffd27a",
          stroke: "#1a1420",
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1);
      root.add(tag);
    } else if (e.role === "hero") {
      tag = this.scene.add
        .text(0, -diam / 2 - 18, "★", {
          fontFamily: "Segoe UI, Arial, sans-serif",
          fontSize: "12px",
          color: "#b388ff",
          stroke: "#1a1420",
          strokeThickness: 3,
        })
        .setOrigin(0.5, 1);
      root.add(tag);
    }

    if (e.hp >= e.maxHp && e.role !== "master" && e.role !== "hero" && e.role !== "weapon") {
      hpBg.setVisible(false);
      hpGhost.setVisible(false);
      hpFill.setVisible(false);
    }
    return {
      root,
      body,
      rig,
      hpBg,
      hpGhost,
      hpFill,
      tag,
      dying: false,
      radius: e.radius,
      spriteId,
      affiliation,
      teamId: e.teamId,
      hpColor: teamColor(e.teamId),
      animState: e.animState,
      animTime: e.animTime,
      aimAngle: e.aimAngle,
      lastFrameKey: key,
      role: e.role,
    };
  }

  private updateHpBar(view: EntityView, ratio: number, always: boolean): void {
    const r = Math.max(0, Math.min(1, ratio));
    const show = always || r < 0.999;
    updateHpBar(
      { bg: view.hpBg, ghost: view.hpGhost, fill: view.hpFill },
      {
        ratio: r,
        visible: show,
        fillColor: hpFillColor(view.teamId, r),
      },
    );
  }
}
